import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { addDays, dateKey, startOfDay, weekday } from '../../lib/date'
import './HabitDateStrip.scss'

interface HabitDateStripProps {
  selectedDate: Date
  today: Date
  onSelect: (date: Date) => void
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/**
 * Horizontal 7-day strip centered on today. Arrows page by whole weeks; the
 * date-picker jump is deferred (step 6). Selecting a day is controlled by the
 * parent; the visible window only shifts via the arrows.
 */
export function HabitDateStrip({ selectedDate, today, onSelect }: HabitDateStripProps) {
  const { t } = useTranslation()
  // Window start shifts only via arrows — selecting a day doesn't re-center.
  const [windowStart, setWindowStart] = useState(() => addDays(startOfDay(today), -3))

  const days = Array.from({ length: 7 }, (_, i) => addDays(windowStart, i))
  const selectedKey = dateKey(selectedDate)
  const todayKey = dateKey(today)
  const monthLabel = selectedDate
    .toLocaleDateString(undefined, { month: 'short' })
    .toUpperCase()
  const yearLabel = String(selectedDate.getFullYear()).slice(-2)

  return (
    <div className="habit-date-strip">
      <div className="habit-date-strip__nav">
        <button
          type="button"
          className="habit-date-strip__arrow"
          aria-label={t('common.previous')}
          onClick={() => setWindowStart(addDays(windowStart, -7))}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          className="habit-date-strip__arrow"
          aria-label={t('common.next')}
          onClick={() => setWindowStart(addDays(windowStart, 7))}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="habit-date-strip__days">
        {days.map((day) => {
          const key = dateKey(day)
          const classes = [
            'habit-date-strip__day',
            key === selectedKey ? 'habit-date-strip__day--selected' : '',
            key === todayKey ? 'habit-date-strip__day--today' : '',
          ].join(' ')
          return (
            <button key={key} type="button" className={classes} onClick={() => onSelect(day)}>
              <span className="habit-date-strip__num">{day.getDate()}</span>
              <span className="habit-date-strip__wd">{t(`habits.weekdays.${WEEKDAY_KEYS[weekday(day)]}`)}</span>
            </button>
          )
        })}
      </div>

      <div className="habit-date-strip__month">
        <strong>{monthLabel}</strong> {yearLabel}
      </div>
    </div>
  )
}
